import React, { FC, useMemo, useContext } from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { ComponentState } from "types/Component";
import ProcessusLink from "composants/processus/ProcessusLink";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import { GalaxieContext } from "containers/galaxy/GalaxieContext";
import {
  ProcessusDefinitionNature,
  ProcessusDefinition,
  ProcessusAffectation,
} from "types/Processus";

interface InteractiveReportProcessusLinkProps {
  sjmoCode?: string;
  target?: string;
  selectedEntities?: any;
}

interface ProcessusLinkRendererProps extends Partial<CustomCellRendererProps> {
  definition: ComponentState;
  onAfterSaveProcess?: () => void;
}

const ProcessusLinkRenderer: FC<ProcessusLinkRendererProps> = ({
  definition,
  value,
  data,
  onAfterSaveProcess,
  ...props
}) => {
  const galaxyProps = useContext(GalaxieContext);
  // Récupération des options du composant
  const processusType = (definition.options?.type as ProcessusDefinitionNature) || "edition";
  const sjmoCode = definition.options?.sjmoCode;
  const target = definition.options?.target;

  // Parse les options depuis le value (qui peut être un JSON string)
  const providerOptions: InteractiveReportProcessusLinkProps & {
    selectedEntities?: any;
  } = useMemo(() => {
    try {
      if (target && typeof value === "string" && value.includes("selectedEntities")) {
        return JSON.parse(value);
      }
      return {};
    } catch (error) {
      console.error(error);
      return {};
    }
  }, [value]);

  // Création de la définition du processus
  const processusDefinition = useMemo(() => {
    return {
      id: definition.options?.id || "",
      type: processusType,
      label: definition.options?.label || "",
      affectation: ProcessusAffectation.GLOBAL,
      needEntity: true,
      apercu: processusType === "edition" ? definition.options?.apercu : undefined,
      rapide: processusType === "edition" ? definition.options?.rapide : undefined,
      forAll: processusType === "traitement" ? false : undefined,
      isAdvanced: definition.options?.isAdvanced || false,
      isOneByOne: false,
    } as ProcessusDefinition;
  }, [
    definition.options?.id,
    definition.options?.label,
    definition.options?.apercu,
    definition.options?.rapide,
    definition.options?.isAdvanced,
    processusType,
  ]);

  // Détermination du sjmoCode et tableName à utiliser
  const finalSjmoCode = galaxyProps?.sjmoCode || providerOptions.sjmoCode || sjmoCode || "DEFAULT";
  const finalTableName = target || providerOptions.tableName || "default";

  // Normalisation de selectedEntities pour ProcessusProvider
  // Si target n'est pas défini dans les options et que selectedEntities n'est pas fourni,
  // on utilise les données de la ligne complète
  const normalizedSelected = useMemo(() => {
    let selected = providerOptions.selectedEntities;

    // Si target n'est pas défini et que selectedEntities n'est pas fourni, utiliser les données de la ligne
    if (!target && data) {
      selected = { ...data };
    }

    if (!selected) return undefined;
    if (Array.isArray(selected)) return selected;
    // Si c'est un objet, on le convertit en tableau de 1 élément
    return [selected];
  }, [providerOptions.selectedEntities, target, data]);

  if (value === null) {
    return <div />;
  }

  return (
    <ProcessusProvider
      sjmoCode={finalSjmoCode}
      tableName={finalTableName}
      selected={normalizedSelected}
      onAfterSaveProcess={onAfterSaveProcess}
    >
      <ProcessusLink
        definition={processusDefinition}
        className={definition.options?.className}
        canDelayProcess
      />
    </ProcessusProvider>
  );
};

export default ProcessusLinkRenderer;
